import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Authenticate } from '../models/auth/authenticate';
import { MemoryService } from '../services/memory.service';
import { HttpError } from './http-error';

/**
 * Utilisé pour intercepter les requêtes HTTP afin de les enrichir et de gérer les reponses d'erreur.
 */
@Injectable()
export class CustomHttpInterceptor implements HttpInterceptor {
    constructor(
        public memoryService: MemoryService,
        private router: Router
    ) {}

    /**
     * Intercepte les requetes
     */
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const user: Authenticate = this.memoryService.getConnectedUser();

        // Si le header skipAuthentication est présent dans la requête cela indique que l'on ne veut pas ajouter le token d'authentification (ex : dans le cas d'un appel à une API externe)
        if (!req.headers.get('skipAuthentication')) {
            if (!!user) {
                req = req.clone({
                    setHeaders: {
                        Authorization: `Bearer ${user.token.accessToken}`,
                    },
                });
            }
        } else {
            // On retire le header skipAuthentication qui peut poser des problèmes avec la CORS Policy du serveur.
            req = req.clone({
                headers: req.headers.delete('skipAuthentication'),
            });
        }
        return next.handle(req).pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
    }

    /**
     * Gère une réponse en erreur.
     */
    public handleError(error: HttpErrorResponse) {
        let httpError: HttpError;

        if (error.error instanceof ErrorEvent) {
            // client-side error
            httpError = new HttpError(0, 'Browser error', error.error.message);
        } else {
            if (error.status === 0) {
                httpError = new HttpError(502, 'Server unavailable', 'Le serveur Hub4B ne répond pas; veuillez contacter votre administrateur');
            } else if (error.status === 401 || error.status === 402) {
                if (!error.url.endsWith('api/auth/logon')) {
                    console.error("Le serveur a retournée une erreur lié à l'authentification", error);
                    httpError = new HttpError(error.status, this.getErrorPhrase(error), this.getErrorMessage(error), error.error);
                    this.memoryService.setConnectedUser(null);
                    this.memoryService.selectOrganization(null);
                    this.router.navigate(['/login']);
                }
            } else if (error.status === 403) {
                console.error('error', error);
                httpError = new HttpError(error.status, this.getErrorPhrase(error), this.getErrorMessage(error), error.error);
            } else if (error.status === 409) {
                try {
                    const json = JSON.parse(error.error);

                    if (json.type === 'duplicate') {
                        httpError = new HttpError(error.status, 'Doublon', ` Un élément existe déjà avec une propriété '${json.propertyName}' identique`, error.error);
                    } else if (json.type === 'necessary') {
                        httpError = new HttpError(
                            error.status,
                            'Nécessaire',
                            ` Un élément avec une propriété '${json.propertyName}' est nécessaire au fonctionnement de l'application`,
                            error.error
                        );
                    } else if (json.type === 'used') {
                        httpError = new HttpError(error.status, 'Utilisé', json.message, error.error);
                    } else if (json.type === 'badSettings') {
                        httpError = new HttpError(error.status, 'Paramétrage incorrect', json.message, error.error);
                    } else if (json.type === 'keyword') {
                        httpError = new HttpError(error.status, 'Mot reservé', json.message, error.error);
                    } else if (json.type === 'system') {
                        httpError = new HttpError(error.status, 'Element système', json.message, error.error);
                    } else if (json.type === 'alreadyConnected') {
                        httpError = new HttpError(error.status, 'Une autre connexion est active', json.message, error.error);
                    } else if (json.type === 'update-aborted-used-as-sending-address-per-template') {
                        httpError = new HttpError(error.status, "L'adresse e-mail est utilisée comme adresse émettrice par un modèle de mail.", json.message, error.error);
                    } else if (json.type === 'update-aborted-used-as-sending-address-per-view') {
                        httpError = new HttpError(error.status, "L'adresse e-mail est utilisée comme adresse émettrice par une vue.", json.message, error.error);
                    } else if (json.type === 'update-aborted-used-as-receiving-address-per-channel') {
                        httpError = new HttpError(
                            error.status,
                            "L'adresse e-mail est utilisée comme adresse de reception d'un canal de reception. Elle ne peut pas être désactivée.",
                            json.message,
                            error.error
                        );
                    } else if (json.type === 'deletion-aborted-used-as-sending-address-per-template') {
                        httpError = new HttpError(error.status, "L'adresse e-mail est utilisée comme adresse émettrice par un modèle de mail.", json.message, error.error);
                    } else if (json.type === 'deletion-aborted-used-as-sending-address-per-view') {
                        httpError = new HttpError(error.status, "L'adresse e-mail est utilisée comme adresse émettrice par une vue.", json.message, error.error);
                    } else if (json.type === 'deletion-aborted-used-as-receiving-address-per-channel') {
                        httpError = new HttpError(error.status, "L'adresse e-mail est utilisée comme adresse de reception d'un canal de reception.", json.message, error.error);
                    } else {
                        console.error('error', error);
                        httpError = new HttpError(error.status, this.getErrorPhrase(error), this.getErrorMessage(error), error.error);
                    }
                } catch (e) {
                    // Erreur levé en cas de JSON incorrect.
                    console.error('error', error);
                    httpError = new HttpError(error.status, this.getErrorPhrase(error), this.getErrorMessage(error));
                }
            } else if (error.status === 400) {
                if (!error.url.endsWith('api/auth/logon')) {
                    console.error('error', error);
                    httpError = new HttpError(error.status, this.getErrorPhrase(error), this.getErrorMessage(error));
                }
            } else {
                console.error('error', error);
                httpError = new HttpError(error.status, this.getErrorPhrase(error), this.getErrorMessage(error));
            }
        }

        return throwError(httpError);
    }

    /**
     * Retourne un message d'erreur à afficher par l'UI en fonction du code retour de la réponse.
     * @param error Erreur de la réponse.
     */
    private getErrorMessage(error: HttpErrorResponse) {
        switch (error.status) {
            case 404:
                return 'La ressource demandée est introuvable.\r\nveuillez contacter votre administrateur.';
            case 400:
            case 409:
                return error.error;
            case 403:
                return "Vous n'êtes pas autorisé à effectuer cette action.";
            case 401:
            case 402:
                return "Vous n'êtes pas authentifié.\r\nVous allez être redirigé sur la page d'authentification";
            case 503:
                return 'Le service est momentanément indisponible.\r\nVeillez essayer ultérieuremnt.';
            case 500:
                return 'Le serveur a rencontré un problème.\r\nVeuillez contacter votre administrateur.';
            case 501:
                return "L'archivage n'a pas été activé pour votre organisation\r\nVeuillez contacter votre administrateur.";
            default:
                return 'Veuillez contacter votre administrateur.';
        }
    }

    private getErrorPhrase(error: HttpErrorResponse) {
        switch (error.status) {
            case 404:
                return 'Not Found';
            case 400:
                return 'Bad Request';
            case 409:
                return 'Conflict';
            case 403:
                return 'Forbidden';
            case 401:
            case 402:
                return 'Unauthorized';
            case 503:
                return 'Service Unavailable';
            case 500:
                return 'Internal Server Error';
            case 501:
                return 'Not Implemented';
            default:
                return 'Error';
        }
    }
}
